import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientMovementForm } from "../../../store/slices/clientMovementFormSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function ClientMovementRequestFormPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentClientMovementForm, isLoading, error } = useSelector(
    (state) => state.clientMovementForms
  );

  useEffect(() => {
    if (id) dispatch(fetchClientMovementForm(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentClientMovementForm) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentClientMovementForm, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Client Movement Request Form print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentClientMovementForm) return null;

  const d = currentClientMovementForm;

  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  return (
    <>
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: "Times New Roman", Times, serif !important;
        }

        body {
          background: #fff;
          color: #000;
          font-size: 11pt !important;
          line-height: 1.4 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 10px !important;
          width: 100% !important;
          max-width: 800px;
          margin: 0 auto;
        }
        ${printFooterStyles}

        .header-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 5px;
        }

        .header-logo img {
          height: 60px;
        }

        .header-text {
          text-align: center;
          margin-bottom: 25px;
        }

        .header-text h2 {
          font-size: 16pt !important;
          font-weight: bold !important;
          margin: 0 0 2px 0 !important;
          text-transform: uppercase;
        }

        .header-text h3 {
          font-size: 13pt !important;
          font-weight: bold !important;
          margin: 0 !important;
        }

        .info-list {
          list-style: none;
          padding: 0;
          margin: 0 0 15px 0;
        }

        .info-list li {
          display: flex;
          margin-bottom: 10px;
          align-items: baseline;
        }

        .info-label {
          font-weight: bold;
          min-width: 250px;
          flex-shrink: 0;
        }

        .info-val-underline {
          border-bottom: 1px dotted #000;
          flex-grow: 1;
          padding-left: 5px;
          padding-bottom: 1px;
          font-weight: normal;
        }

        .agreement-box {
          border: 1px solid #000;
          padding: 15px;
          margin: 20px 0;
          background-color: #fafafa;
        }

        .agreement-title {
          font-weight: bold;
          font-size: 12pt;
          margin-bottom: 10px;
          text-decoration: underline;
        }

        .agreement-text {
          font-size: 10.5pt;
          line-height: 1.5;
          text-align: justify;
        }

        .clause-list {
          margin-top: 10px;
          padding-left: 20px;
        }

        .clause-list li {
          margin-bottom: 8px;
        }

        .checkbox-container {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #000;
          text-align: center;
          line-height: 12px;
          font-size: 10pt;
          margin-right: 8px;
        }

        .signature-section {
          margin-top: 30px;
        }

        .sig-row {
          display: flex;
          margin-top: 15px;
          align-items: flex-end;
        }

        .sig-label {
          font-weight: bold;
          min-width: 150px;
        }

        .sig-line {
          border-bottom: 1px dotted #000;
          flex-grow: 1;
          padding-left: 5px;
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="header-logo"><img src={logo} alt="The Recovery House Logo" /></div>
          <div className="header-text">
            <h2>THE RECOVERY HOUSE (TRH)</h2>
            <h3>📋 Client Movement Request Form</h3>
          </div>

        {/* Info Fields */}
        <ul className="info-list">
          <li>
            <span className="info-label">• Client Name:</span>
            <span className="info-val-underline">{d.client_name || ""}</span>
          </li>
          <li>
            <span className="info-label">• Date of Movement Request:</span>
            <span className="info-val-underline">{fmt(d.date_of_movement_request)}</span>
          </li>
          <li>
            <span className="info-label">• Purpose of Visit for (Official work, Barber, etc.):</span>
            <span className="info-val-underline">{d.purpose_of_visit || ""}</span>
          </li>
          <li>
            <span className="info-label">• Requested Date for Movement:</span>
            <span className="info-val-underline">{fmt(d.requested_date)}</span>
          </li>
          <li>
            <span className="info-label">• Requested Time for Movement:</span>
            <span className="info-val-underline">{d.requested_time || ""}</span>
          </li>
          <li>
            <span className="info-label">• Estimated Duration (in hours):</span>
            <span className="info-val-underline">{d.estimated_duration || ""}</span>
          </li>
          <li>
            <span className="info-label">• Family Member Name (Requesting):</span>
            <span className="info-val-underline">{d.family_member_name || ""}</span>
          </li>
          <li>
            <span className="info-label">• Relationship with Client:</span>
            <span className="info-val-underline">{d.relationship || ""}</span>
          </li>
          <li>
            <span className="info-label">• Contact Number:</span>
            <span className="info-val-underline">{d.contact_number || ""}</span>
          </li>
        </ul>

        {/* Agreement Block */}
        <div className="agreement-box">
          <div className="agreement-title">Agreement</div>
          <div className="agreement-item">
            <span className="agreement-check">✓</span>
            <span>Only off-duty nursing staff will be assigned.</span>
          </div>
          <div className="agreement-item">
            <span className="agreement-check">✓</span>
            <span>Charges of PKR 1000/hour will apply.</span>
          </div>
          <div className="agreement-item">
            <span className="agreement-check">✓</span>
            <span>Payment will be made after the visit at the reception.</span>
          </div>
        </div>

        {/* Finance Information */}
        <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "11pt", borderBottom: "1px solid #000", paddingBottom: "2px", marginBottom: "10px" }}>
          Finance Information
        </div>
        <ul className="info-list">
          <li>
            <span className="info-label">• Amount:</span>
            <span className="info-val-underline">{d.amount || "—"}</span>
          </li>
          <li>
            <span className="info-label">• Payment Status:</span>
            <span className="info-val-underline">{d.payment_status || "—"}</span>
          </li>
          <li>
            <span className="info-label">• Reference No:</span>
            <span className="info-val-underline">{d.reference_no || "—"}</span>
          </li>
          <li>
            <span className="info-label">• Payment Date:</span>
            <span className="info-val-underline">{fmt(d.payment_date) || "—"}</span>
          </li>
        </ul>

        {/* Signature Block */}
        <div className="signature-section">
          <div className="sig-row">
            <span className="sig-label">Signature:</span>
            <span className="sig-line">{d.signature || ""}</span>
          </div>
          <p style={{ fontSize: "9pt", color: "#555", marginTop: "5px", fontStyle: "italic" }}>
            (Typed name is acceptable for WhatsApp submission)
          </p>
        </div>

      </div>
      </PrintWrapper>
    </>
  );
}
